import SEO from '../components/SEO';

const Section = ({ title, children }) => (
  <div className="mt-12">
    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 tracking-tight">{title}</h2>
    <div className="space-y-4 text-gray-700 leading-relaxed">{children}</div>
  </div>
);

const About = () => (
  <>
    <SEO
      title="About Beyond Ball India"
      description="Beyond Ball India is building the digital infrastructure for Indian basketball — helping players discover tournaments, organizers reach audiences, and the community stay connected."
      path="/about"
    />

    <div className="pt-24 pb-16 min-h-screen">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-xs font-semibold text-[#F97316] uppercase tracking-wider mb-2">About us</div>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-gray-900 mb-3 animate-fade-up">
          More than a tournament platform.
        </h1>
        <p className="text-lg text-gray-600 mb-10 animate-fade-up delay-100">
          Beyond Ball India started long before this website existed.
        </p>

        <div className="space-y-5 text-gray-700 leading-relaxed text-base sm:text-lg animate-fade-up delay-200">
          <p>It began with a simple frustration: finding opportunities in Indian basketball shouldn't be this difficult.</p>
          <p>For years, players across India have struggled to discover tournaments, connect with organizers, find opportunities, and stay updated with what is happening in the basketball ecosystem. Information was often scattered across WhatsApp groups, Instagram stories, and personal networks.</p>
          <p className="font-semibold text-gray-900">Many talented players simply missed opportunities because they never knew they existed.</p>
        </div>

        <Section title="Our journey">
          <p>Beyond Ball India was originally launched as a basketball-focused media platform dedicated to covering Indian basketball stories, tournaments, players, and community developments.</p>
          <p>Through content creation, interviews, event coverage, and storytelling, we have built a growing community of basketball players, coaches, organizers, and fans from across the country.</p>
          <p>As the community grew, one thing became clear:</p>
          <p className="text-gray-900 font-semibold border-l-2 border-[#F97316] pl-4 italic">
            Indian basketball didn't just need more content. It needed better infrastructure.
          </p>
        </Section>

        <Section title="Why we built this platform">
          <p>Every week, players ask:</p>
          <ul className="list-none space-y-1.5 text-gray-700">
            <li>· Where can I find tournaments?</li>
            <li>· Are there any events happening near me?</li>
            <li>· How do I contact organizers?</li>
            <li>· How can my team register?</li>
          </ul>
          <p className="mt-4">At the same time, organizers struggle to:</p>
          <ul className="list-none space-y-1.5 text-gray-700">
            <li>· Reach the right audience</li>
            <li>· Manage registrations</li>
            <li>· Build credibility</li>
            <li>· Promote events effectively</li>
          </ul>
          <p className="mt-4 font-semibold text-gray-900">This platform was built to solve those problems.</p>
        </Section>

        <Section title="What Beyond Ball India does">
          <p>Beyond Ball India helps:</p>
          <ul className="space-y-2.5 mt-3">
            <li className="flex items-start gap-3"><span className="text-2xl leading-none">🏀</span><span><strong className="text-gray-900">Players</strong> discover opportunities</span></li>
            <li className="flex items-start gap-3"><span className="text-2xl leading-none">🏀</span><span><strong className="text-gray-900">Teams</strong> find competitions</span></li>
            <li className="flex items-start gap-3"><span className="text-2xl leading-none">🏀</span><span><strong className="text-gray-900">Organizers</strong> reach more participants</span></li>
            <li className="flex items-start gap-3"><span className="text-2xl leading-none">🏀</span><span>The <strong className="text-gray-900">basketball community</strong> stay connected</span></li>
          </ul>
          <p className="mt-4">Our goal is to make basketball opportunities easier to discover and participate in.</p>
        </Section>

        <Section title="Our mission">
          <p>To create the digital infrastructure that helps Indian basketball grow.</p>
          <p>We want every player, regardless of city or background, to have access to opportunities that were previously difficult to find.</p>
        </Section>

        <Section title="Our vision">
          <p>We envision a future where every basketball tournament, camp, league, tryout, and opportunity in India can be discovered through a single trusted platform.</p>
          <p>A future where no player misses an opportunity simply because they never heard about it.</p>
        </Section>

        <div className="mt-16 bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3 tracking-tight">This is just the beginning.</h2>
          <p className="text-gray-300 leading-relaxed max-w-xl mx-auto">
            The current platform is only the first step. Over time, Beyond Ball India aims to build tools, resources, and systems that support the growth of players, organizers, teams, and the entire basketball ecosystem.
          </p>
        </div>
      </div>
    </div>
  </>
);

export default About;
